import { useContext } from "react";
import { withManagedProps } from "@made-people/managed-props";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import { ResponsiveVideo } from "@made-people/blueprint-responsive-video";
import { useLink } from "@made-people/grease-link";
import { ProductListCellMetricsContext } from "@made-people/blueprint-product-list";

type ProductListBannerAsset = {
  filename?: string;
  src?: string;
  alt?: string;
};

type ProductListBannerImage = ProductListBannerAsset & {
  mobile?: ProductListBannerAsset;
  desktop?: ProductListBannerAsset;
};

type ProductListBannerVideo = string | { mobile?: string; desktop?: string };

type ProductListBannerProps = {
  image?: ProductListBannerImage;
  desktopImage?: ProductListBannerAsset;
  video?: ProductListBannerVideo;
  desktopVideo?: string;
  title?: string;
  titleTypographyColor?: string;
  description?: string;
  descriptionTypographyColor?: string;
  link?: { cached_url?: string; url?: string; gearsUrl?: string };
  linkButtonText?: string;
  linkButtonTypographyColor?: string;
  linkButtonBackgroundColor?: string;
  linkButtonOpacity?: number;
};

const toImage = (asset?: ProductListBannerAsset) => {
  const src = asset?.src || asset?.filename;
  return src ? { src, alt: asset?.alt || "" } : undefined;
};

const toVideoSrc = (value?: string) =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const toVideoSources = (video?: ProductListBannerVideo) =>
  typeof video === "string"
    ? { mobile: toVideoSrc(video), desktop: undefined }
    : {
        mobile: toVideoSrc(video?.mobile),
        desktop: toVideoSrc(video?.desktop),
      };

export const ProductListBanner = withManagedProps(
  "ProductListBanner",
  ({
    image,
    desktopImage,
    video,
    desktopVideo,
    title,
    titleTypographyColor,
    description,
    descriptionTypographyColor,
    link,
    linkButtonText,
    linkButtonTypographyColor,
    linkButtonBackgroundColor,
    linkButtonOpacity,
  }: ProductListBannerProps) => {
    const Link = useLink();
    const cellMetrics = useContext(ProductListCellMetricsContext);
    const href = link?.gearsUrl || link?.cached_url || link?.url;

    const mobileImage = toImage(image?.mobile) || toImage(image);
    const desktopImageSrc = toImage(desktopImage) || toImage(image?.desktop);
    const smallImage = mobileImage || desktopImageSrc;
    const largeImage = desktopImageSrc || mobileImage;

    const videoSources = toVideoSources(video);
    const mobileVideoSrc = videoSources.mobile;
    const desktopVideoSrc = toVideoSrc(desktopVideo) || videoSources.desktop;
    const smallVideoSrc = mobileVideoSrc || desktopVideoSrc;
    const largeVideoSrc = desktopVideoSrc || mobileVideoSrc;
    const hasVideo = Boolean(smallVideoSrc || largeVideoSrc);

    const children = (
      <>
        <div
          className="invisible w-full"
          style={{
            aspectRatio:
              cellMetrics?.aspectRatio ?? "var(--aspect-ratio-product-image)",
          }}
        />
        {hasVideo ? (
          <ResponsiveVideo
            src={{ mobile: smallVideoSrc, desktop: largeVideoSrc }}
            poster={smallImage?.src}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          smallImage && (
            <ResponsiveImage
              initial={smallImage}
              lg={largeImage}
              displaySize={cellMetrics?.displaySize ?? "50vw"}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )
        )}
        {(title || description || (href && linkButtonText)) && (
          <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 flex-col items-center gap-2 p-4 text-center lg:gap-3 lg:p-6">
            {(title || description) && (
              <div className="flex flex-col items-center gap-2 lg:gap-3">
                {title && (
                  <h4
                    className="font-secondary text-heading-lg lg:text-heading-2xl"
                    style={{ color: titleTypographyColor || "white" }}
                  >
                    {title}
                  </h4>
                )}
                {description && (
                  <p
                    className="text-heading-sm lg:text-heading-lg"
                    style={{ color: descriptionTypographyColor || "white" }}
                  >
                    {description}
                  </p>
                )}
              </div>
            )}
            {href && linkButtonText && (
              <Link
                href={href}
                className="inline-block rounded px-6 py-3 text-label-sm uppercase"
                style={{
                  color: linkButtonTypographyColor || "black",
                  backgroundColor: `color-mix(in srgb, ${
                    linkButtonBackgroundColor || "white"
                  } ${linkButtonOpacity ?? 100}%, transparent)`,
                }}
              >
                {linkButtonText}
              </Link>
            )}
          </div>
        )}
      </>
    );

    return (
      <div className="relative w-full self-start overflow-hidden rounded-lg !h-auto lg:!h-[var(--plp-banner-height)]">
        {children}
      </div>
    );
  },
);
